import type { z } from 'zod';
import {
  ClientMessageSchema,
  ServerMessageSchema,
  type ClientMessage,
  type ServerMessage,
} from './protocol';

/**
 * Wire encoding for realtime messages. JSON for now; this module is the single place to swap
 * in a binary format if profiling shows snapshot size or parse time matters.
 */
export type DecodeResult<T> = { ok: true; message: T } | { ok: false; reason: string };

const decodeWith =
  <T>(schema: z.ZodType<T>) =>
  (raw: string): DecodeResult<T> => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { ok: false, reason: 'invalid JSON' };
    }
    const result = schema.safeParse(parsed);
    return result.success
      ? { ok: true, message: result.data }
      : { ok: false, reason: result.error.message };
  };

export const encodeClientMessage = (message: ClientMessage): string => JSON.stringify(message);
export const encodeServerMessage = (message: ServerMessage): string => JSON.stringify(message);

export const decodeClientMessage: (raw: string) => DecodeResult<ClientMessage> =
  decodeWith(ClientMessageSchema);
export const decodeServerMessage: (raw: string) => DecodeResult<ServerMessage> =
  decodeWith(ServerMessageSchema);
